import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from './api';

function PollVolunteerPage() {
  const { accessToken } = useParams();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [volunteer, setVolunteer] = useState(null);
  const [stats, setStats] = useState(null);
  const [voteTracking, setVoteTracking] = useState(null);
  const [manualVotes, setManualVotes] = useState([]);
  const [allVolunteersCounts, setAllVolunteersCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('stats'); // 'stats', 'tracking', or 'manual'
  const [isViewOnly, setIsViewOnly] = useState(false);  // NEW: Track if using audit password (view-only)
  const [manualVoteEntry, setManualVoteEntry] = useState({ 
    voteType: 'topic',  // 'topic' or 'candidate'
    itemIndex: 0, 
    itemId: '',  // For dropdown value (motion index or candidate ID)
    answer: '', 
    count: 1 
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/api/vote/poll-volunteer/authenticate', {
        access_token: accessToken,
        password: password
      });

      setVolunteer(response.data.volunteer);
      setAuthenticated(true);
      // Track if this is view-only access (audit password)
      const canSubmit = response.data.can_submit !== false && response.data.access_type !== 'view_only';
      setIsViewOnly(!canSubmit);
      loadStats();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid password or access expired');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.post(`/api/vote/poll-volunteer/${accessToken}/stats`, {
        password: password
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error loading stats:', err);
      setError('Failed to load statistics');
    }
  };

  const loadVoteTracking = async () => {
    const electionId = stats?.election?.id;
    if (!electionId) return;
    
    try {
      const response = await api.get(`/api/vote/election/${electionId}/vote-tracking`, {
        params: {
          access_token: accessToken,
        },
      });
      setVoteTracking(response.data);
    } catch (err) {
      console.error('Error loading vote tracking:', err);
    }
  };

  const loadManualVotes = async () => {
    try {
      const response = await api.post(`/api/vote/poll-volunteer/${accessToken}/manual-votes`, password);
      setManualVotes(response.data.manual_votes || []);
    } catch (err) {
      console.error('Error loading manual votes:', err);
    }
  };

  const loadAllVolunteersCounts = async () => {
    try {
      const response = await api.post(`/api/vote/poll-volunteer/${accessToken}/all-volunteers-counts`, password);
      setAllVolunteersCounts(response.data);
    } catch (err) {
      console.error('Error loading all volunteers counts:', err);
    }
  };

  const handleAddManualVote = async (e) => {
    e.preventDefault();
    // Prevent submission in view-only mode
    if (isViewOnly) {
      alert('View-only mode: You cannot submit manual votes with the audit password.');
      return;
    }
    try {
      await api.post(`/api/vote/poll-volunteer/${accessToken}/manual-vote`, {
        password: password,
        vote_type: manualVoteEntry.voteType,
        item_index: manualVoteEntry.itemIndex,
        answer: manualVoteEntry.answer,
        count: parseInt(manualVoteEntry.count)
      });
      
      alert(`✅ ${manualVoteEntry.count} manual vote(s) recorded`);
      
      // Reset form
      setManualVoteEntry({ voteType: 'topic', itemIndex: 0, itemId: '', answer: '', count: 1 });
      
      // Reload manual votes, stats, and comparison data
      loadManualVotes();
      loadStats();
      loadAllVolunteersCounts();
    } catch (err) {
      alert('Failed to add manual vote: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Helper function to check if counts match across volunteers
  const getCountAgreement = (voteType, itemId, answer, currentCount) => {
    if (!allVolunteersCounts || !allVolunteersCounts.volunteers || allVolunteersCounts.volunteers.length < 2) {
      return { status: 'single', message: 'Only one volunteer has entered data' };
    }

    const counts = [];
    
    allVolunteersCounts.volunteers.forEach(vol => {
      if (voteType === 'topic') {
        // For topics: itemId is motion_index, check answer count
        const topicData = vol.topics[itemId];
        if (topicData && topicData[answer] !== undefined) {
          counts.push({ volunteer: vol.volunteer_name, count: topicData[answer] });
        }
      } else if (voteType === 'candidate') {
        // For candidates: itemId is candidate_id
        const candidateCount = vol.candidates[itemId];
        if (candidateCount !== undefined) {
          counts.push({ volunteer: vol.volunteer_name, count: candidateCount });
        }
      }
    });

    if (counts.length === 0) {
      return { status: 'no_data', message: 'No data entered yet' };
    }

    if (counts.length === 1) {
      return { status: 'single', message: `Only ${counts[0].volunteer} has entered this count` };
    }

    // Check if all counts match
    const allCountsMatch = counts.every(c => c.count === counts[0].count);
    
    if (allCountsMatch) {
      return { 
        status: 'match', 
        message: `✅ All ${counts.length} volunteers agree: ${counts[0].count}`,
        counts 
      };
    } else {
      return { 
        status: 'mismatch', 
        message: `⚠️ Mismatch detected`,
        counts,
        details: counts.map(c => `${c.volunteer}: ${c.count}`).join(', ')
      };
    }
  };

  useEffect(() => {
    if (authenticated) {
      loadStats();
      const interval = setInterval(loadStats, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [authenticated]);

  useEffect(() => {
    if (authenticated && stats) {
      loadVoteTracking();
      loadManualVotes();
      loadAllVolunteersCounts();
      const interval = setInterval(() => {
        loadVoteTracking();
        loadManualVotes();
        loadAllVolunteersCounts();
      }, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [authenticated, stats]);

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🗳️</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Poll Volunteer Access</h1>
            <p className="text-gray-600 mt-2">Enter your temporary password to view live voting statistics</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Temporary Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter password"
                required
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !password}
              className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
            >
              {loading ? 'Authenticating...' : 'Access Statistics'}
            </button>
          </form>

          <p className="text-xs text-center text-gray-500 mt-6">
            This access link was provided by your HOA board for election monitoring purposes.
          </p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const election = stats.election;
  const isOpen = stats.vote_status.is_open;
  const meetingHasStarted = stats.vote_status.meeting_has_started;
  const eVotingClosed = stats.vote_status.e_voting_closed;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* View-Only Banner - Show when using audit password */}
      {isViewOnly && (
        <div className="bg-yellow-100 border-b-2 border-yellow-400 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <span className="text-2xl">👁️</span>
            <div>
              <p className="font-semibold text-yellow-800">View-Only Mode (Audit Access)</p>
              <p className="text-sm text-yellow-700">
                You are logged in with the audit password. You can view all statistics but cannot submit or modify any vote counts.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">{election.name}</h1>
              <p className="text-blue-100 mt-1">Poll Volunteer Dashboard {isViewOnly ? '(View-Only)' : ''}</p>
            </div>
            <div className="flex flex-col gap-2">
              <div className={`px-4 py-2 rounded-full font-medium text-center ${
                isOpen ? 'bg-green-500' : 'bg-red-500'
              }`}>
                {isOpen ? '🟢 E-Voting Open' : eVotingClosed ? '🔴 E-Voting Closed' : '⏳ Not Started'}
              </div>
              {meetingHasStarted && (
                <div className="px-4 py-2 rounded-full font-medium text-center bg-yellow-500">
                  🏛️ Meeting Started
                </div>
              )}
            </div>
          </div>
          <p className="text-sm text-blue-100 mt-3">
            Welcome, {volunteer.name} | Access expires: {stats.volunteer_access_expires_hoa || new Date(stats.volunteer_access_expires).toLocaleString()}
          </p>
          {stats.vote_status.meeting_start_time && (
            <p className="text-sm text-blue-100 mt-1">
              Meeting Time: {stats.vote_status.meeting_start_time_hoa || new Date(stats.vote_status.meeting_start_time).toLocaleString()} | 
              E-Voting closes 10 minutes after meeting starts
            </p>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'stats'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📊 Live Statistics
            </button>
            <button
              onClick={() => setActiveTab('tracking')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'tracking'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📋 Who Voted (Tracking)
            </button>
            <button
              onClick={() => setActiveTab('manual')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'manual'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ✍️ Manual Vote Entry
            </button>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {activeTab === 'stats' && (
          <>
        {/* Vote Status */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold mb-4">📊 Vote Status</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-3xl font-bold text-blue-900">{stats.total_votes_cast}</div>
              <div className="text-sm text-blue-700">Total Votes Cast</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-3xl font-bold text-green-900">{stats.total_properties_voted}</div>
              <div className="text-sm text-green-700">Properties Voted</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-3xl font-bold text-purple-900">{stats.active_proxies.length}</div>
              <div className="text-sm text-purple-700">Active Proxies</div>
            </div>
          </div>
        </div>

        {/* Your Collected Counts */}
        <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-green-400 rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-green-900">📋 Your Collected Counts</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 border-2 border-green-300">
              <div className="text-4xl font-bold text-green-900">{stats.total_ballots_collected || 0}</div>
              <div className="text-sm text-gray-700 mt-1">Physical Ballots</div>
            </div>
            <div className="bg-white rounded-lg p-4 border-2 border-blue-300">
              <div className="text-4xl font-bold text-blue-900">{stats.voting_proxies_collected || 0}</div>
              <div className="text-sm text-gray-700 mt-1">Voting Proxies</div>
              <div className="text-xs text-blue-600">(also counts for quorum)</div>
            </div>
            <div className="bg-white rounded-lg p-4 border-2 border-purple-300">
              <div className="text-4xl font-bold text-purple-900">{stats.quorum_proxies_collected || 0}</div>
              <div className="text-sm text-gray-700 mt-1">Quorum-Only Proxies</div>
              <div className="text-xs text-purple-600">(no vote cast)</div>
            </div>
          </div>
          <div className="mt-3 bg-gray-100 rounded-lg p-2 text-center">
            <span className="text-sm text-gray-700">
              <strong>Total for Quorum:</strong> {(stats.voting_proxies_collected || 0) + (stats.quorum_proxies_collected || 0)} proxies
            </span>
          </div>
          <p className="text-xs text-gray-600 mt-2">
            These counts will be included in the final turnout calculation for this election.
          </p>
        </div>

        {/* Motions Results */}
        {!meetingHasStarted && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="text-3xl">🔒</span>
              <div>
                <h2 className="text-xl font-semibold text-yellow-900 mb-2">Vote Totals Hidden</h2>
                <p className="text-yellow-800">
                  Vote results are <strong>anonymous</strong> and will only be visible after the meeting starts.
                  <br/>
                  <strong>Meeting Start Time:</strong> {stats.vote_status.meeting_start_time ? new Date(stats.vote_status.meeting_start_time).toLocaleString() : 'Not set'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {meetingHasStarted && stats.topic_results && stats.topic_results.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">📋 {eVotingClosed ? 'Final Vote Results' : 'Live Motion Results'}</h2>
              {eVotingClosed && (
                <span className="px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                  🔴 E-Voting Closed - Collecting Manual Votes
                </span>
              )}
            </div>
            <div className="space-y-4">
              {stats.topic_results.map((topic, idx) => {
                const hasDisagreement = topic.manual_agreement === false;
                
                return (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">{topic.topic_name}</h3>
                    
                    {/* Disagreement Warning */}
                    {hasDisagreement && (
                      <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm font-semibold text-yellow-900">⚠️ Totals do not agree for manual votes - please recount</p>
                        <p className="text-xs text-yellow-700 mt-1">Using smaller count as conservative basis until volunteers agree</p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-3 gap-3">
                      {Object.entries(topic.answer_counts).map(([answer, totalCount]) => {
                        const eVotes = topic.e_vote_counts?.[answer] || 0;
                        const manualVotes = topic.manual_vote_counts?.[answer] || 0;
                        
                        return (
                          <div key={answer} className="bg-gray-50 rounded p-3 text-center">
                            <div className="text-2xl font-bold text-indigo-600">{totalCount}</div>
                            {eVotingClosed && (eVotes > 0 || manualVotes > 0) && (
                              <div className="text-xs text-gray-500">
                                E: {eVotes} + Manual: {manualVotes}
                              </div>
                            )}
                            <div className="text-xs text-gray-600">{answer}</div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Per-Volunteer Manual Counts */}
                    {eVotingClosed && topic.volunteer_manual_counts && topic.volunteer_manual_counts.length > 0 && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <h4 className="text-xs font-semibold text-gray-700 mb-2">Volunteer Manual Counts:</h4>
                        <div className="space-y-1">
                          {topic.volunteer_manual_counts.map((vol, volIdx) => (
                            <div key={volIdx} className="text-xs text-gray-600 flex justify-between">
                              <span className="font-medium">{vol.volunteer_name}:</span>
                              <span>
                                {Object.entries(vol.counts).map(([ans, cnt]) => `${ans}: ${cnt}`).join(', ')} 
                                <span className="ml-2 font-semibold">(Total: {vol.total})</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div className="text-center">
                          <div className="font-semibold text-blue-900">{topic.total_e_votes || 0}</div>
                          <div className="text-xs text-blue-700">E-Votes</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-purple-900">{topic.total_manual_votes || 0}</div>
                          <div className="text-xs text-purple-700">Manual Votes {hasDisagreement ? '(Conservative)' : ''}</div>
                        </div>
                        <div className="text-center">
                          <div className="font-bold text-green-900 text-lg">{topic.total_votes || 0}</div>
                          <div className="text-xs text-green-700 font-semibold">TOTAL VOTES</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Candidate Results */}
        {meetingHasStarted && stats.candidate_results && stats.candidate_results.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">🏆 {eVotingClosed ? 'Final Candidate Results' : 'Live Candidate Results'}</h2>
              {eVotingClosed && (
                <span className="px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                  🔴 E-Voting Closed - Collecting Manual Votes
                </span>
              )}
            </div>
            <div className="space-y-4">
              {stats.candidate_results.map((candidate, idx) => {
                // Calculate proxy votes: total - e_votes - manual_votes
                const proxyVotes = (candidate.total_votes || 0) - (candidate.e_votes || 0) - (candidate.manual_votes || 0);
                
                return (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gradient-to-r from-blue-50 to-purple-50">
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">{candidate.candidate_name}</h3>
                    <p className="text-sm text-gray-600">{candidate.position || 'Board Position'}</p>
                  </div>
                  
                  <div className="grid grid-cols-5 gap-3">
                    <div className="bg-white rounded-lg p-4 text-center border border-blue-200">
                      <div className="text-3xl font-bold text-blue-600">{candidate.e_votes}</div>
                      <div className="text-xs text-blue-700 font-medium mt-1">E-Votes</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center border border-indigo-200">
                      <div className="text-3xl font-bold text-indigo-600">{proxyVotes}</div>
                      <div className="text-xs text-indigo-700 font-medium mt-1">Proxy Votes</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center border border-purple-200">
                      <div className="text-3xl font-bold text-purple-600">{candidate.manual_votes}</div>
                      <div className="text-xs text-purple-700 font-medium mt-1">Manual Votes</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center border border-green-200">
                      <div className="text-3xl font-bold text-green-600">{candidate.total_votes}</div>
                      <div className="text-xs text-green-700 font-medium mt-1">TOTAL</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-4 text-center text-white">
                      <div className="text-2xl font-bold">{candidate.total_votes}</div>
                      <div className="text-xs font-medium mt-1">Final Count</div>
                    </div>
                  </div>
                  
                  <div className="mt-3 text-sm text-gray-700 bg-white rounded p-3 border border-gray-200">
                    <strong>{candidate.candidate_name}</strong> received <strong>{candidate.e_votes} e-votes</strong>{proxyVotes > 0 && <> + <strong>{proxyVotes} proxy votes</strong></>} + <strong>{candidate.manual_votes} manual votes</strong> = <strong className="text-green-600">{candidate.total_votes} total votes</strong>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Who Voted */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold mb-4">✅ Voters</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Lot/Unit</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Time</th>
                </tr>
              </thead>
              <tbody>
                {stats.voter_details.map((voter, idx) => (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">{voter.name}</td>
                    <td className="py-3 px-4 text-sm">{voter.lot}</td>
                    <td className="py-3 px-4 text-sm">
                      {voter.is_proxy_vote ? (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">Proxy Vote</span>
                      ) : (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Direct Vote</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(voter.voted_at).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Active Proxies */}
        {stats.active_proxies && stats.active_proxies.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-xl font-semibold mb-4">🤝 Active Proxies</h2>
            <div className="space-y-2">
              {stats.active_proxies.map((proxy, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-sm">
                    <strong>{proxy.originator_name}</strong> → <strong>{proxy.holder_name}</strong>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${
                    proxy.proxy_type === 'general' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {proxy.proxy_type}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
          </>
        )}

        {activeTab === 'tracking' && voteTracking && (
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold mb-2">📋 Vote Tracking - Manual Count Prevention</h2>
              <p className="text-sm text-gray-600">
                Use this list to track which properties have voted to prevent double counting during manual vote collection.
              </p>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-900">{voteTracking.statistics.total_properties}</div>
                <div className="text-sm text-blue-700">Total Properties</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-900">{voteTracking.statistics.voted}</div>
                <div className="text-sm text-green-700">Voted</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-3xl font-bold text-orange-900">{voteTracking.statistics.not_voted}</div>
                <div className="text-sm text-orange-700">Not Voted</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-3xl font-bold text-purple-900">{voteTracking.statistics.turnout_percentage}%</div>
                <div className="text-sm text-purple-700">Turnout</div>
              </div>
            </div>

            {/* Vote Tracking Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Property ID</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Address</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Eligibility</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Voted</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Proxies Given</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Proxies Received</th>
                  </tr>
                </thead>
                <tbody>
                  {voteTracking.tracking_list.map((property, idx) => (
                    <tr key={idx} className={`border-b border-gray-100 ${
                      property.has_voted ? 'bg-green-50' : property.is_suspended ? 'bg-red-50' : 'hover:bg-gray-50'
                    }`}>
                      <td className="py-3 px-4 text-sm font-semibold text-blue-900">{property.property_identifier}</td>
                      <td className="py-3 px-4 text-sm">{property.property_address}</td>
                      <td className="py-3 px-4 text-center">
                        {property.is_suspended ? (
                          <span className="inline-flex items-center px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                            🚫 Suspended
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            ✅ Eligible
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {property.has_voted ? (
                          <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            ✅ Voted
                            {property.vote_timestamp && (
                              <span className="ml-2 text-xs text-green-600">
                                {new Date(property.vote_timestamp).toLocaleTimeString()}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                            ⏳ Not Voted
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {property.proxies_given_count > 0 ? (
                          <div className="text-xs space-y-1">
                            {property.proxies_given.map((p, i) => (
                              <div key={i} className="text-purple-700">
                                → {p.proxy_to} ({p.proxy_to_address})
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">None</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {property.proxies_received_count > 0 ? (
                          <div className="text-xs space-y-1">
                            {property.proxies_received.map((p, i) => (
                              <div key={i} className="text-blue-700">
                                ← {p.proxy_from} ({p.proxy_from_address})
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">None</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Legend */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold mb-2">Legend:</h3>
              <div className="text-xs text-gray-600 space-y-1">
                <div>• <strong>✅ Voted:</strong> Property has already cast their vote (no double counting)</div>
                <div>• <strong>⏳ Not Voted:</strong> Property has not voted yet</div>
                <div>• <strong>Can Vote = No:</strong> Property gave their proxy to someone else (cannot vote directly)</div>
                <div>• <strong>Proxies Received:</strong> This property holder can vote on behalf of other properties</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'manual' && (
          <>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="mb-6">
                <h2 className="text-2xl font-semibold mb-2">✍️ Manual Vote Entry</h2>
                <p className="text-sm text-gray-600">
                  Record paper ballot votes collected in person. These will be added to the online voting totals.
                </p>
              </div>

              {/* CRITICAL INSTRUCTIONS */}
              <div className="mb-6 space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                  <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2">
                    <span>🚨</span> CRITICAL: Prevent Double Counting
                  </h3>
                  <ul className="text-sm text-red-800 space-y-2">
                    <li>• <strong>NEVER count manual votes for properties that already e-voted</strong></li>
                    <li>• Check the &quot;Who Voted (Tracking)&quot; tab to see which <strong>property identifiers</strong> already voted online</li>
                    <li>• Only accept manual votes from properties marked as &quot;⏳ Not Voted&quot;</li>
                    <li>• Each property can only vote ONCE (either online OR in-person, never both)</li>
                  </ul>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                    <span>✅</span> Mail/In-Person Ballot Processing Steps
                  </h3>
                  <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                    <li><strong>Receive envelope</strong> - Collect sealed ballot from property owner</li>
                    <li><strong>Check property identifier</strong> - Note the lot/unit number on envelope</li>
                    <li><strong>Cross-check online voting</strong> - Look up property in &quot;Who Voted&quot; tab</li>
                    <li><strong>Verify eligibility</strong> - Check if property is on eligible voters list</li>
                    <li>
                      <strong>Decision point:</strong>
                      <ul className="ml-4 mt-1 space-y-1">
                        <li>✅ If NOT voted online, NOT given proxy, AND eligible → Remove ballot from envelope and add to counting pile</li>
                        <li>❌ If already voted online OR gave proxy OR not eligible → Set envelope aside (do not count)</li>
                      </ul>
                    </li>
                    <li><strong>Count ballots</strong> - Tally each answer on your score sheet</li>
                    <li><strong>Enter totals in system</strong> - Record your counts + quorum proxies collected</li>
                  </ol>
                </div>

                <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg">
                  <h3 className="font-bold text-purple-900 mb-2 flex items-center gap-2">
                    <span>📍</span> Property Identifiers
                  </h3>
                  <p className="text-sm text-purple-800">
                    Our system uses <strong>property identifiers (lot/unit numbers)</strong> for voting tracking, not individual names. 
                    Multiple homeowners may be listed for one property, but only ONE vote per property is allowed.
                  </p>
                </div>
              </div>

              {/* Total Ballot Count Entry */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-bold text-green-900 mb-4 flex items-center gap-2">
                  <span>📊</span> Record Your Total Counts
                </h3>
                <p className="text-sm text-gray-700 mb-4">
                  Before entering individual vote tallies, record the total number of ballots and proxies you collected.
                </p>
                
                {/* View-Only Warning for Ballot Entry Section */}
                {isViewOnly && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-4">
                    <p className="text-sm text-yellow-800">
                      👁️ <strong>View-Only Mode:</strong> You cannot modify ballot or proxy counts with the audit password.
                    </p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Total Ballots Collected */}
                  <div className="bg-white rounded-lg p-4 border border-green-300">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      📄 Total Ballots Collected
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="e.g., 45"
                      defaultValue={stats.total_ballots_collected || ''}
                      disabled={isViewOnly}
                      className={`w-full text-2xl font-bold border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-green-500 focus:ring-2 focus:ring-green-200 ${isViewOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      onBlur={async (e) => {
                        if (isViewOnly) return; // Prevent submission in view-only mode
                        const value = e.target.value.trim();
                        if (value === '') return; // Skip if empty
                        
                        const count = parseInt(value) || 0;
                        try {
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vote/poll-volunteer/${accessToken}/update-ballot-count`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ 
                              password: password,  // Fixed: was volunteerPassword
                              total_ballots_collected: count 
                            })
                          });
                          
                          if (!response.ok) {
                            const error = await response.json();
                            throw new Error(error.detail || 'Failed to save');
                          }
                          
                          console.log('✅ Total ballot count updated:', count);
                        } catch (error) {
                          console.error('Failed to update ballot count:', error);
                          alert('Failed to save ballot count: ' + error.message);
                        }
                      }}
                    />
                    <p className="text-xs text-gray-600 mt-2">
                      Number of physical ballots you counted from envelopes
                    </p>
                  </div>

                  {/* Voting Proxies Collected (also count for quorum) */}
                  <div className="bg-white rounded-lg p-4 border border-blue-300">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      🗳️ Voting Proxies Collected
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="e.g., 5"
                      disabled={isViewOnly}
                      className={`w-full text-2xl font-bold border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${isViewOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      defaultValue={stats.voting_proxies_collected || ''}
                      onBlur={async (e) => {
                        if (isViewOnly) return; // Prevent submission in view-only mode
                        const value = e.target.value.trim();
                        if (value === '') return;
                        
                        const count = parseInt(value) || 0;
                        try {
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vote/poll-volunteer/${accessToken}/update-ballot-count`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ 
                              password: password,
                              voting_proxies_collected: count 
                            })
                          });
                          
                          if (!response.ok) {
                            const error = await response.json();
                            throw new Error(error.detail || 'Failed to save');
                          }
                          
                          console.log('✅ Voting proxy count updated:', count);
                        } catch (error) {
                          console.error('Failed to update voting proxy count:', error);
                          alert('Failed to save voting proxy count: ' + error.message);
                        }
                      }}
                    />
                    <p className="text-xs text-gray-600 mt-2">
                      Physical voting proxies (these <strong>also count for quorum</strong>)
                    </p>
                  </div>

                  {/* Quorum-Only Proxies Collected */}
                  <div className="bg-white rounded-lg p-4 border border-green-300">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      🏛️ Quorum-Only Proxies Collected
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="e.g., 3"
                      disabled={isViewOnly}
                      className={`w-full text-2xl font-bold border-2 border-gray-300 rounded-lg px-4 py-3 focus:border-green-500 focus:ring-2 focus:ring-green-200 ${isViewOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      defaultValue={stats.quorum_proxies_collected || ''}
                      onBlur={async (e) => {
                        if (isViewOnly) return; // Prevent submission in view-only mode
                        const value = e.target.value.trim();
                        if (value === '') return;
                        
                        const count = parseInt(value) || 0;
                        try {
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vote/poll-volunteer/${accessToken}/update-ballot-count`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ 
                              password: password,
                              quorum_proxies_collected: count 
                            })
                          });
                          
                          if (!response.ok) {
                            const error = await response.json();
                            throw new Error(error.detail || 'Failed to save');
                          }
                          
                          console.log('✅ Quorum-only proxy count updated:', count);
                        } catch (error) {
                          console.error('Failed to update quorum proxy count:', error);
                          alert('Failed to save quorum proxy count: ' + error.message);
                        }
                      }}
                    />
                    <p className="text-xs text-gray-600 mt-2">
                      Quorum-only proxies (NO vote cast, for attendance only)
                    </p>
                  </div>
                </div>

                <div className="mt-4 bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                  <p className="text-xs text-yellow-900">
                    ⚠️ <strong>Important:</strong> Do NOT count proxies that were already assigned online. 
                    Only count physical proxy forms you collected that are NOT in the system.<br/>
                    <strong>Note:</strong> Voting proxies automatically count toward quorum. Total quorum = Voting Proxies + Quorum-Only Proxies.
                  </p>
                </div>
              </div>

              {/* Manual Vote Entry Form */}
              <form onSubmit={handleAddManualVote} className={`bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 ${isViewOnly ? 'opacity-50' : ''}`}>
                <h3 className="text-lg font-semibold mb-4 text-blue-900">Add Manual Votes</h3>
                {isViewOnly ? (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-4">
                    <p className="text-sm text-yellow-800">
                      👁️ <strong>View-Only Mode:</strong> You cannot add manual votes with the audit password.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mb-2">Enter paper ballot counts for candidates and motions. Your entry will override any previous count you entered for the same item.</p>
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
                      💡 <strong>Tip:</strong> To remove/delete an entry you made by mistake, re-enter the same item with a count of <strong>0</strong>. There is no formal delete - entering 0 effectively removes your count.
                    </p>
                  </>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Item</label>
                    <select
                      value={manualVoteEntry.itemId}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.startsWith('topic-')) {
                          const idx = parseInt(value.replace('topic-', ''));
                          setManualVoteEntry({...manualVoteEntry, itemId: value, voteType: 'topic', itemIndex: idx, answer: ''});
                        } else if (value.startsWith('candidate-')) {
                          // FIXED: Use replace instead of split to handle UUIDs with dashes
                          const candidateId = value.replace('candidate-', '');
                          setManualVoteEntry({...manualVoteEntry, itemId: value, voteType: 'candidate', itemIndex: 0, answer: candidateId});
                        }
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select item...</option>
                      {/* Candidates */}
                      {election.candidates && election.candidates.length > 0 && (
                        <optgroup label="Candidates">
                          {election.candidates.map((candidate, idx) => (
                            <option key={`candidate-${idx}`} value={`candidate-${candidate.id}`}>
                              {candidate.position || 'Board Position'}: {candidate.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {/* Topics/Motions */}
                      {election.special_topics && election.special_topics.length > 0 && (
                        <optgroup label="Questions/Motions">
                          {election.special_topics.map((topic, idx) => (
                            <option key={`topic-${idx}`} value={`topic-${idx}`}>
                              {topic.name || `Question ${idx + 1}`}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {manualVoteEntry.voteType === 'candidate' ? 'Vote Count' : 'Answer'}
                    </label>
                    {manualVoteEntry.voteType === 'candidate' ? (
                      <input
                        type="number"
                        min="0"
                        value={manualVoteEntry.count}
                        onChange={(e) => setManualVoteEntry({...manualVoteEntry, count: parseInt(e.target.value) || 0})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter count"
                        required
                      />
                    ) : (
                      <select
                        value={manualVoteEntry.answer}
                        onChange={(e) => setManualVoteEntry({...manualVoteEntry, answer: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select answer...</option>
                        {election.special_topics && 
                         election.special_topics[manualVoteEntry.itemIndex]?.answers?.map((answer, idx) => (
                          <option key={idx} value={answer}>{answer}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {manualVoteEntry.voteType === 'topic' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Count</label>
                      <input
                        type="number"
                        min="0"
                        value={manualVoteEntry.count}
                        onChange={(e) => setManualVoteEntry({...manualVoteEntry, count: parseInt(e.target.value) || 0})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  )}

                  <div className="flex items-end">
                    <button
                      type="submit"
                      disabled={isViewOnly}
                      className={`w-full px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors ${isViewOnly ? 'bg-gray-400 cursor-not-allowed hover:bg-gray-400' : ''}`}
                    >
                      {isViewOnly ? '👁️ View-Only' : '➕ Add Votes'}
                    </button>
                  </div>
                </div>
              </form>

              {/* Manual Votes Summary */}
              <div>
                <h3 className="text-lg font-semibold mb-4">📝 Your Manual Vote Entries ({manualVotes.length})</h3>
                <p className="text-sm text-gray-600 mb-2">Your paper ballot counts with volunteer agreement indicators.</p>
                <div className="text-xs text-gray-500 mb-4 p-2 bg-gray-50 rounded border border-gray-200">
                  <strong>Agreement Legend:</strong>
                  <span className="ml-2">✅ Match = All volunteers agree</span>
                  <span className="ml-2">⚠️ Mismatch = Counts differ between volunteers</span>
                  <span className="ml-2 text-amber-600">⚠️ Review = Only you entered this (others may have entered different answers)</span>
                </div>
                
                {manualVotes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No manual votes entered yet.</p>
                    <p className="text-sm mt-2">Use the form above to record paper ballot votes.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Type</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Manual Vote Date</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Item</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Selection</th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Count</th>
                          <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Agreement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {manualVotes.map((vote, idx) => {
                          const voteDate = vote.updated_at || vote.created_at || vote.entered_at;
                          const formattedDate = voteDate ? new Date(voteDate).toLocaleString() : 'N/A';
                          
                          // Determine item name based on vote type
                          let itemName = '';
                          let selection = '';
                          let itemId = '';
                          
                          if (vote.vote_type === 'candidate') {
                            // Find candidate by ID
                            const candidate = election.candidates?.find(c => c.id === vote.candidate_id);
                            itemName = candidate ? `${candidate.position || 'Board Position'}: ${candidate.name}` : 'Unknown Candidate';
                            selection = 'Votes for candidate';
                            itemId = vote.candidate_id;
                          } else {
                            // Topic vote
                            itemName = election.special_topics?.[vote.motion_index]?.name || `Question ${vote.motion_index + 1}`;
                            selection = vote.answer;
                            itemId = vote.motion_index;
                          }
                          
                          // Get agreement status
                          const agreement = getCountAgreement(vote.vote_type, itemId, vote.answer, vote.count);
                          
                          return (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                  vote.vote_type === 'candidate' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {vote.vote_type === 'candidate' ? '👤 Candidate' : '📋 Question'}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600">
                                {formattedDate}
                              </td>
                              <td className="py-3 px-4 text-sm font-medium">
                                {itemName}
                              </td>
                              <td className="py-3 px-4 text-sm">{selection}</td>
                              <td className="py-3 px-4 text-center">
                                <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                                  {vote.count}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                {agreement.status === 'match' && (
                                  <div className="flex items-center justify-center gap-2">
                                    <span className="text-green-600 text-lg">✅</span>
                                    <span className="text-xs text-green-700 font-medium">Match</span>
                                  </div>
                                )}
                                {agreement.status === 'mismatch' && (
                                  <div className="flex flex-col items-center">
                                    <div className="flex items-center gap-2">
                                      <span className="text-yellow-600 text-lg">⚠️</span>
                                      <span className="text-xs text-yellow-700 font-medium">Mismatch</span>
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1" title={agreement.details}>
                                      {agreement.details}
                                    </div>
                                  </div>
                                )}
                                {agreement.status === 'single' && (
                                  <div className="text-center">
                                    <span className="text-xs text-amber-600 font-medium">⚠️ Review</span>
                                    <div className="text-xs text-gray-500">Only you entered this</div>
                                  </div>
                                )}
                                {agreement.status === 'no_data' && (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr className="border-t-2 border-gray-300">
                          <td colSpan="4" className="py-3 px-4 text-right font-semibold">Total Manual Votes:</td>
                          <td colSpan="2" className="py-3 px-4 text-center">
                            <span className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-900 rounded-full text-base font-bold">
                              {manualVotes.reduce((sum, vote) => sum + vote.count, 0)}
                            </span>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>

              {/* All Volunteers Comparison */}
              {allVolunteersCounts && allVolunteersCounts.volunteers && allVolunteersCounts.volunteers.length > 1 && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-6 mt-6">
                  <h3 className="text-lg font-semibold mb-4 text-blue-900 flex items-center gap-2">
                    <span>👥</span> All Volunteers Count Summary
                  </h3>
                  <p className="text-sm text-gray-700 mb-4">
                    Cross-reference counts from all volunteers to ensure accuracy. Mismatches require recounting.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {allVolunteersCounts.volunteers.map((vol, idx) => {
                      // Calculate total counts for this volunteer
                      let topicTotal = 0;
                      Object.values(vol.topics).forEach(topic => {
                        Object.values(topic).forEach(count => {
                          topicTotal += count;
                        });
                      });
                      
                      let candidateTotal = 0;
                      Object.values(vol.candidates).forEach(count => {
                        candidateTotal += count;
                      });
                      
                      const grandTotal = topicTotal + candidateTotal;
                      
                      return (
                        <div key={idx} className="bg-white rounded-lg p-4 border border-blue-200 shadow-sm">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-2xl">👤</span>
                            <div>
                              <h4 className="font-semibold text-gray-900">{vol.volunteer_name}</h4>
                              <p className="text-xs text-gray-500">Volunteer {idx + 1}</p>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Topic Votes:</span>
                              <span className="font-semibold text-blue-900">{topicTotal}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Candidate Votes:</span>
                              <span className="font-semibold text-purple-900">{candidateTotal}</span>
                            </div>
                            <div className="border-t pt-2 mt-2 flex justify-between">
                              <span className="text-sm font-semibold text-gray-700">Total:</span>
                              <span className="text-lg font-bold text-green-900">{grandTotal}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                    <p className="text-xs text-yellow-900">
                      💡 <strong>Note:</strong> The system uses the MINIMUM count when volunteers disagree to ensure conservative, accurate results. Please recount any items showing a mismatch (⚠️) indicator.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PollVolunteerPage;
